import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../services/auth.service';
import { toast } from 'react-toastify';

interface ValidationErrors {
  [key: string]: string[];
}

interface ApiErrorResponse {
  message: string;
  errors?: ValidationErrors;
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    firm_name: '',
    firm_address: '',
    firm_city: '',
    firm_state: '',
    firm_zip: '',
    firm_phone: '',
    firm_website: '',
    agreeToTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isRegistered, setIsRegistered] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.agreeToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await authService.register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        firm_name: formData.firm_name,
        firm_address: formData.firm_address,
        firm_city: formData.firm_city,
        firm_state: formData.firm_state,
        firm_zip: formData.firm_zip,
        firm_phone: formData.firm_phone,
        firm_website: formData.firm_website,
      });
      setIsRegistered(true);
      toast.success('Registration successful! Please wait for admin approval.');
    } catch (error: any) {
      const errorResponse = error.response?.data as ApiErrorResponse;

      if (errorResponse?.errors) {
        setErrors(errorResponse.errors);
        // Show the first error message as a toast
        const firstErrorField = Object.keys(errorResponse.errors)[0];
        const firstError = errorResponse.errors[firstErrorField][0];
        toast.error(firstError);
      } else {
        toast.error(errorResponse?.message || 'Failed to register');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: string) => {
    try {
      const redirectUrl = await authService.socialLogin(provider);
      window.location.href = redirectUrl;
    } catch (error: any) {
      toast.error(`Failed to initialize ${provider} login`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for the field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const nextStep = () => {
    // Basic validation for user details
    if (currentStep === 1) {
      if (!formData.name) {
        setErrors(prev => ({ ...prev, name: ['Name is required'] }));
        return;
      }
      if (!formData.email) {
        setErrors(prev => ({ ...prev, email: ['Email is required'] }));
        return;
      }
      if (!formData.password) {
        setErrors(prev => ({ ...prev, password: ['Password is required'] }));
        return;
      }
      if (formData.password !== formData.password_confirmation) {
        setErrors(prev => ({
          ...prev,
          password_confirmation: ['Passwords do not match'],
        }));
        return;
      }
    }

    // Basic validation for firm details
    if (currentStep === 2) {
      if (!formData.firm_name) {
        setErrors(prev => ({ ...prev, firm_name: ['Firm name is required'] }));
        return;
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  if (isRegistered) {
    return (
      <div className='text-center'>
        <div className='mb-4'>
          <i
            className='ri-checkbox-circle-line text-success'
            style={{ fontSize: '48px' }}
          ></i>
        </div>
        <h4 className='fw-semibold mb-2'>Registration Received</h4>
        <p className='mb-4'>
          Thank you for registering your law firm! Your account is pending
          approval from our administrators. You will receive an email
          notification once your account has been approved.
        </p>
        <p className='mb-4'>
          Please note that this process may take up to 24 hours.
        </p>
        <Link to='/login' className='btn btn-primary'>
          Return to Login
        </Link>
      </div>
    );
  }

  return (
    <>
      <h4 className='fw-semibold mb-2'>Create Your Account</h4>
      <p className='mb-4 text-muted op-7 fw-normal'>
        Join our platform to manage your law firm&apos;s cases and documents
      </p>

      <div className='btn-list'>
        <button
          className='btn btn-light'
          onClick={() => handleSocialLogin('google')}
          type='button'
        >
          <svg
            className='google-svg'
            xmlns='http://www.w3.org/2000/svg'
            width='2443'
            height='2500'
            preserveAspectRatio='xMidYMid'
            viewBox='0 0 256 262'
          >
            <path
              fill='#4285F4'
              d='M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027'
            />
            <path
              fill='#34A853'
              d='M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1'
            />
            <path
              fill='#FBBC05'
              d='M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782'
            />
            <path
              fill='#EB4335'
              d='M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251'
            />
          </svg>
          Sign up with Google
        </button>
        <button
          className='btn btn-icon btn-light'
          onClick={() => handleSocialLogin('microsoft')}
          type='button'
        >
          <i className='ri-microsoft-fill'></i>
        </button>
      </div>

      <div className='text-center my-5 authentication-barrier'>
        <span>OR</span>
      </div>

      <form onSubmit={handleSubmit}>
        {currentStep === 1 && (
          <>
            <h5 className='mb-3'>Account Information</h5>
            <div className='mb-3'>
              <label htmlFor='name' className='form-label'>
                Full Name
              </label>
              <input
                type='text'
                className={`form-control form-control-lg ${errors.name ? 'is-invalid' : ''}`}
                id='name'
                name='name'
                placeholder='e.g., John Smith'
                value={formData.name}
                onChange={handleChange}
                required
              />
              {errors.name && (
                <div className='invalid-feedback'>{errors.name[0]}</div>
              )}
            </div>

            <div className='mb-3'>
              <label htmlFor='email' className='form-label'>
                Email
              </label>
              <input
                type='email'
                className={`form-control form-control-lg ${errors.email ? 'is-invalid' : ''}`}
                id='email'
                name='email'
                placeholder='john@lawfirm.com'
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && (
                <div className='invalid-feedback'>{errors.email[0]}</div>
              )}
            </div>

            <div className='mb-3'>
              <label htmlFor='password' className='form-label'>
                Password
              </label>
              <input
                type='password'
                className={`form-control form-control-lg ${errors.password ? 'is-invalid' : ''}`}
                id='password'
                name='password'
                placeholder='Minimum 8 characters'
                value={formData.password}
                onChange={handleChange}
                required
              />
              {errors.password && (
                <div className='invalid-feedback'>{errors.password[0]}</div>
              )}
            </div>

            <div className='mb-4'>
              <label htmlFor='password_confirmation' className='form-label'>
                Confirm Password
              </label>
              <input
                type='password'
                className={`form-control form-control-lg ${errors.password_confirmation ? 'is-invalid' : ''}`}
                id='password_confirmation'
                name='password_confirmation'
                placeholder='Re-enter your password'
                value={formData.password_confirmation}
                onChange={handleChange}
                required
              />
              {errors.password_confirmation && (
                <div className='invalid-feedback'>
                  {errors.password_confirmation[0]}
                </div>
              )}
            </div>

            <button
              type='button'
              className='btn btn-primary btn-lg w-100'
              onClick={nextStep}
            >
              Next: Firm Details
            </button>
          </>
        )}

        {currentStep === 2 && (
          <>
            <h5 className='mb-3'>Law Firm Information</h5>
            <div className='mb-3'>
              <label htmlFor='firm_name' className='form-label'>
                Firm Name
              </label>
              <input
                type='text'
                className={`form-control form-control-lg ${errors.firm_name ? 'is-invalid' : ''}`}
                id='firm_name'
                name='firm_name'
                placeholder='e.g., Smith & Associates Law Firm'
                value={formData.firm_name}
                onChange={handleChange}
                required
              />
              {errors.firm_name && (
                <div className='invalid-feedback'>{errors.firm_name[0]}</div>
              )}
            </div>

            <div className='mb-3'>
              <label htmlFor='firm_address' className='form-label'>
                Address
              </label>
              <input
                type='text'
                className={`form-control form-control-lg ${errors.firm_address ? 'is-invalid' : ''}`}
                id='firm_address'
                name='firm_address'
                placeholder='123 Main Street, Suite 100'
                value={formData.firm_address}
                onChange={handleChange}
              />
              {errors.firm_address && (
                <div className='invalid-feedback'>{errors.firm_address[0]}</div>
              )}
            </div>

            <div className='row mb-3'>
              <div className='col-md-6'>
                <label htmlFor='firm_city' className='form-label'>
                  City
                </label>
                <input
                  type='text'
                  className={`form-control form-control-lg ${errors.firm_city ? 'is-invalid' : ''}`}
                  id='firm_city'
                  name='firm_city'
                  placeholder='New York'
                  value={formData.firm_city}
                  onChange={handleChange}
                />
                {errors.firm_city && (
                  <div className='invalid-feedback'>{errors.firm_city[0]}</div>
                )}
              </div>
              <div className='col-md-3'>
                <label htmlFor='firm_state' className='form-label'>
                  State
                </label>
                <input
                  type='text'
                  className={`form-control form-control-lg ${errors.firm_state ? 'is-invalid' : ''}`}
                  id='firm_state'
                  name='firm_state'
                  placeholder='NY'
                  value={formData.firm_state}
                  onChange={handleChange}
                />
                {errors.firm_state && (
                  <div className='invalid-feedback'>{errors.firm_state[0]}</div>
                )}
              </div>
              <div className='col-md-3'>
                <label htmlFor='firm_zip' className='form-label'>
                  ZIP
                </label>
                <input
                  type='text'
                  className={`form-control form-control-lg ${errors.firm_zip ? 'is-invalid' : ''}`}
                  id='firm_zip'
                  name='firm_zip'
                  placeholder='10001'
                  value={formData.firm_zip}
                  onChange={handleChange}
                />
                {errors.firm_zip && (
                  <div className='invalid-feedback'>{errors.firm_zip[0]}</div>
                )}
              </div>
            </div>

            <div className='mb-3'>
              <label htmlFor='firm_phone' className='form-label'>
                Phone
              </label>
              <input
                type='text'
                className={`form-control form-control-lg ${errors.firm_phone ? 'is-invalid' : ''}`}
                id='firm_phone'
                name='firm_phone'
                placeholder='(555) 123-4567'
                value={formData.firm_phone}
                onChange={handleChange}
              />
              {errors.firm_phone && (
                <div className='invalid-feedback'>{errors.firm_phone[0]}</div>
              )}
            </div>

            <div className='mb-4'>
              <label htmlFor='firm_website' className='form-label'>
                Website
              </label>
              <input
                type='url'
                className={`form-control form-control-lg ${errors.firm_website ? 'is-invalid' : ''}`}
                id='firm_website'
                name='firm_website'
                placeholder='https://www.yourlawfirm.com'
                value={formData.firm_website}
                onChange={handleChange}
              />
              {errors.firm_website && (
                <div className='invalid-feedback'>{errors.firm_website[0]}</div>
              )}
            </div>

            <div className='d-flex justify-content-between mb-4'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-lg'
                onClick={prevStep}
              >
                Back
              </button>
              <button
                type='button'
                className='btn btn-primary btn-lg text-nowrap'
                onClick={nextStep}
              >
                <span className='d-none d-sm-inline'>
                  Next: Terms & Conditions
                </span>
                <span className='d-sm-none'>Next: Terms</span>
              </button>
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <h5 className='mb-3'>Terms & Conditions</h5>
            <div className='alert alert-info mb-4'>
              <p className='mb-0'>
                By creating an account, you will be registered as a law firm
                administrator. Your account will be pending approval from our
                system administrators. Once approved, you will be able to access
                the system and manage your firm&apos;s cases and documents.
              </p>
            </div>

            <div className='mb-4'>
              <div className='form-check'>
                <input
                  type='checkbox'
                  className={`form-check-input ${errors.agreeToTerms ? 'is-invalid' : ''}`}
                  id='agreeToTerms'
                  name='agreeToTerms'
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  required
                />
                <label className='form-check-label' htmlFor='agreeToTerms'>
                  I agree to the{' '}
                  <Link to='/terms' className='text-primary'>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to='/privacy' className='text-primary'>
                    Privacy Policy
                  </Link>
                </label>
                {errors.agreeToTerms && (
                  <div className='invalid-feedback'>
                    {errors.agreeToTerms[0]}
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex justify-content-between mb-4'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-lg'
                onClick={prevStep}
              >
                Back
              </button>
              <button
                type='submit'
                className='btn btn-primary btn-lg'
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </>
        )}

        <div className='mt-4 text-center'>
          <p className='mb-0'>
            Already have an account?{' '}
            <Link to='/login' className='text-primary'>
              Sign In
            </Link>
          </p>
        </div>
      </form>
    </>
  );
};

export default Register;
